import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [contactInfos, setContactInfos] = useState([]);
  const [heroImage, setHeroImage] = useState({
    image: 'https://images.unsplash.com/photo-1603958956194-cf9718dba4b1',
    title: 'İletişim',
    subtitle: 'Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız'
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    fetchContactInfos();
    loadRecaptcha();
    fetchHeroImage();
  }, []);

  const fetchHeroImage = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php?key=contact_hero_image', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      if (data.setting_value) {
        try {
          const heroData = typeof data.setting_value === 'string' 
            ? JSON.parse(data.setting_value) 
            : data.setting_value;
          if (heroData.image) {
            setHeroImage(heroData);
          }
        } catch (e) {
          console.error('Error parsing hero image:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
    }
  };

  const loadRecaptcha = () => {
    // Google reCAPTCHA script'i yüklenmiş mi kontrol et
    if (window.grecaptcha && window.grecaptcha.render) {
      setRecaptchaLoaded(true);
      
      // Widget'ı render et
      if (recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
        try {
          window.grecaptcha.render(recaptchaRef.current, {
            sitekey: '6Ld-WxIsAAAAAGDYWIdHv8DKcJm2pLC5GWiuh7O4',
            callback: (token) => {
              setRecaptchaToken(token);
            },
            'expired-callback': () => {
              setRecaptchaToken(null);
            },
            'error-callback': () => {
              setRecaptchaToken(null);
              toast.error('reCAPTCHA doğrulaması başarısız. Lütfen tekrar deneyin.');
            },
          });
        } catch (error) {
          console.error('reCAPTCHA render error:', error);
        }
      }
    } else {
      // Script yüklenmesini bekle
      const checkRecaptcha = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          setRecaptchaLoaded(true);
          clearInterval(checkRecaptcha);
          
          // Widget'ı render et
          if (recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
            try {
              window.grecaptcha.render(recaptchaRef.current, {
                sitekey: '6Ld-WxIsAAAAAGDYWIdHv8DKcJm2pLC5GWiuh7O4',
                callback: (token) => {
                  setRecaptchaToken(token);
                },
                'expired-callback': () => {
                  setRecaptchaToken(null);
                },
                'error-callback': () => {
                  setRecaptchaToken(null);
                  toast.error('reCAPTCHA doğrulaması başarısız. Lütfen tekrar deneyin.');
                },
              });
            } catch (error) {
              console.error('reCAPTCHA render error:', error);
            }
          }
        }
      }, 100);

      // 10 saniye sonra timeout
      setTimeout(() => {
        clearInterval(checkRecaptcha);
        if (!window.grecaptcha) {
          console.error('reCAPTCHA yüklenemedi');
        }
      }, 10000);
    }
  };

  useEffect(() => {
    if (recaptchaLoaded && recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
      loadRecaptcha();
    }
  }, [recaptchaLoaded]);

  const fetchContactInfos = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=contact', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching contact infos');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setContactInfos(data);
    } catch (error) {
      console.error('Error fetching contact infos:', error);
    }
  };

  const getIcon = (type) => {
    const icons = {
      address: <MapPin className="w-6 h-6" />,
      phone: <Phone className="w-6 h-6" />,
      email: <Mail className="w-6 h-6" />,
      hours: <Clock className="w-6 h-6" />,
    };
    return icons[type] || <MapPin className="w-6 h-6" />;
  };

  const contactInfo = contactInfos
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
    .map((info) => {
      // Link'i kontrol et - boş string veya null ise null yap
      let link = info.link;
      if (!link || link.trim() === '') {
        link = null;
      }
      
      // Eğer link yoksa ama tip phone veya email ise otomatik link oluştur
      if (!link) {
        if (info.type === 'phone' && info.content) {
          // Telefon numarasından özel karakterleri temizle
          const phoneNumber = info.content.replace(/[^\d+]/g, '');
          link = `tel:${phoneNumber}`;
        } else if (info.type === 'email' && info.content) {
          link = `mailto:${info.content}`;
        }
      }
      
      return {
        icon: getIcon(info.type),
        title: info.title,
        content: info.content,
        link: link,
      };
    });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Lütfen gerekli alanları doldurun');
      return;
    }

    // reCAPTCHA kontrolü
    if (!recaptchaToken) {
      toast.error('Lütfen reCAPTCHA doğrulamasını tamamlayın');
      return;
    }

    try {
      const response = await fetch('http://localhost/api/contact-messages.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptcha_token: recaptchaToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesaj gönderilirken hata oluştu');
        
        // reCAPTCHA hatası ise token'ı sıfırla ve yeniden yükle
        if (errorData.error && errorData.error.includes('reCAPTCHA')) {
          setRecaptchaToken(null);
          if (window.grecaptcha && recaptchaRef.current) {
            try {
              window.grecaptcha.reset();
            } catch (error) {
              console.error('reCAPTCHA reset error:', error);
            }
          }
        }
        return;
      }

      const data = await response.json();
      toast.success(data.message || 'Mesajınız alınmıştır. En kısa sürede size dönüş yapılacaktır.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      
      // Reset reCAPTCHA
      setRecaptchaToken(null);
      if (window.grecaptcha && recaptchaRef.current) {
        try {
          const widgetId = window.grecaptcha.getResponse();
          if (widgetId) {
            window.grecaptcha.reset();
          }
        } catch (error) {
          console.error('reCAPTCHA reset error:', error);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Mesaj gönderilirken hata oluştu');
      
      // reCAPTCHA'yı sıfırla
      setRecaptchaToken(null);
      if (window.grecaptcha && recaptchaRef.current) {
        try {
          window.grecaptcha.reset();
        } catch (error) {
          console.error('reCAPTCHA reset error:', error);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-64 mt-20" data-testid="contact-hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage.image}
            alt={heroImage.title || 'İletişim'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-800/70" />
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 fade-in-up">
              {heroImage.title || 'İletişim'}
            </h1>
            <p className="text-lg text-gray-100 max-w-2xl fade-in-up" style={{ animationDelay: '0.2s' }}>
              {heroImage.subtitle || 'Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-gray-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Bize <span className="gold-gradient-text">Ulaşın</span>
                </h2>
                <p className="text-gray-600">
                  Sorularınızı veya görüşlerinizi bizimle paylaşın
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Adınız Soyadınız *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Adınızı girin"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      E-posta *
                    </label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="ornek@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefon
                    </label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="0532 123 45 67"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Konu
                  </label>
                  <Input
                    data-testid="contact-subject-input"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Mesaj konusu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mesajınız *
                  </label>
                  <Textarea
                    data-testid="contact-message-textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Mesajınızı buraya yazın"
                    rows={6}
                    required
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-start">
                  <div ref={recaptchaRef} id="recaptcha-container"></div>
                  {!recaptchaLoaded && (
                    <div className="text-gray-500 text-sm ml-2">reCAPTCHA yükleniyor...</div>
                  )}
                </div>

                <Button
                  data-testid="submit-contact-btn"
                  type="submit"
                  className="w-full bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold py-3 rounded-full shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!recaptchaToken}
                >
                  <Send size={20} />
                  Mesaj Gönder
                </Button>
              </form>
            </div>

            {/* Map */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Konum
                </h2>
                <p className="text-gray-600">
                  Malatya Yeşilyurt'ta merkezi konumumuz
                </p>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-2xl h-[500px]">
                <iframe
                  src="https://www.google.com/maps?q=SERPİL+ŞAHİN+ORTAOKULU+MALATYA,+Özalper,+Cümle+sokak+no:20,+44090+Yeşilyurt/Malatya&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Serpil Şahin Ortaokulu Malatya Konumu"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                data-testid={`contact-info-${index}`}
                className="p-6 rounded-2xl bg-gray-50 hover:shadow-xl transition-all border border-transparent hover:border-[#C0A062]"
              >
                <div className="w-12 h-12 rounded-full bg-[#C0A062] flex items-center justify-center text-white mb-4">
                  {info.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                {info.link && info.link.trim() !== '' ? (
                  <a
                    href={info.link}
                    className="text-gray-600 hover:text-[#C0A062] transition-colors break-words"
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-gray-600 break-words">{info.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Okulu Ziyaret Etmek İster misiniz?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Kampüsümüzü gezmek ve öğretmen kadromuzla tanışmak için randevu alabilirsiniz.
            Sizi ağırlamaktan mutluluk duyarız!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+903121234567"
              data-testid="visit-call-btn"
              className="px-8 py-4 bg-gradient-to-r from-red-900 to-red-800 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Randevu İçin Arayın
            </a>
            <a
              href="/iletisim"
              data-testid="visit-apply-btn"
              className="px-8 py-4 bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Başvuru Yap
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;